import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, ChevronDown, X, Loader2, Zap, RefreshCw, Search, Lock } from 'lucide-react';
import axiosInstance from '../../api/axios';
import { resolve403ErrorMessage } from '../../request/getApiErrorMessage.ts';
import { isControlUuid } from '../../features/controls/functional.ts';
import { listIssueRunOptions } from '../../api/controlTestRuns';
import { TENANT_PATHS } from '../../routing/paths.ts';

// ─── Shared constants ─────────────────────────────────────────────────────────

export const SEVERITY_CONFIG = {
  observation:            { short: 'OBS', label: 'Observation'            },
  material_weakness:      { short: 'MW',  label: 'Material Weakness'      },
  significant_deficiency: { short: 'SD',  label: 'Significant Deficiency' },
  control_deficiency:     { short: 'CD',  label: 'Control Deficiency'     },
};

export const DEPARTMENTS = [
  'Finance', 'HR', 'IT', 'Operations', 'Accounting', 'Legal', 'Procurement', 'Sales', 'Inventory',
];

export const BASE_CATEGORIES = [
  'Authorization', 'Access Management', 'Segregation of Duties', 'Financial Reporting',
  'Change Management', 'Vendor Management', 'Physical Security', 'Data Integrity', 'Documentation',
];

export const CUSTOM_CATEGORIES_KEY = 'sox_issue_custom_categories';

function buildPeriodOptions() {
  const opts = [];
  const y0 = new Date().getFullYear();
  for (let y = y0 - 2; y <= y0 + 1; y++) {
    opts.push(`FY ${y}`);
    for (let q = 1; q <= 4; q++) opts.push(`Q${q} ${y}`);
  }
  return opts;
}
export const PERIOD_OPTIONS = buildPeriodOptions();

const RUN_STATUS_BADGE = {
  failed:  { label: 'Failed',  cls: 'bg-red-100 text-red-700'    },
  warning: { label: 'Warning', cls: 'bg-amber-100 text-amber-700' },
};

const EMPTY_FORM = {
  title: '', sox_control_ids: [], summary_of_deficiency: '',
  department: '', category: '', period: '', classification: '', issue_type: 'operating_effectiveness',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Reusable create-issue modal with AI drafter flow.
 *
 * Props:
 *  onClose()             – close the modal
 *  onCreated(issue)      – called after the issue is saved
 *  controls              – full control list for the search (pass [] when lockedControl is set)
 *  tenant                – TenantContext value (for org_id in AI calls)
 *  selectedAuditYearId   – current audit year UUID (may be null)
 *  navigateOnCreate      – bool (default true): navigate to issue detail after create
 *
 *  // Locked context (test-run modal):
 *  lockedControl         – { id: UUID, control_id: string, title?: string } | null
 *  lockedTestRun         – { run_number: number, test_run_id?, overall_status?, total_exceptions? } | null
 *  prefillForm           – { title?, period?, category? } | null
 */
export default function CreateIssueModal({
  onClose,
  onCreated,
  controls = [],
  tenant,
  selectedAuditYearId,
  navigateOnCreate = true,
  lockedControl = null,
  lockedTestRun  = null,
  prefillForm    = null,
}) {
  const navigate = useNavigate();

  const [categories, setCategories] = useState(() => {
    try {
      const custom = JSON.parse(localStorage.getItem(CUSTOM_CATEGORIES_KEY) || '[]');
      const all = [...BASE_CATEGORIES];
      custom.forEach(c => { if (!all.includes(c)) all.push(c); });
      return all;
    } catch { return BASE_CATEGORIES; }
  });
  const [newCategoryInput, setNewCategoryInput] = useState('');
  const [showAddCategory,  setShowAddCategory]  = useState(false);

  const initialForm = useMemo(() => {
    const base = { ...EMPTY_FORM };
    if (lockedControl) base.sox_control_ids = [lockedControl.id];
    if (prefillForm?.title)                  base.title                  = prefillForm.title;
    if (prefillForm?.period)                 base.period                 = prefillForm.period;
    if (prefillForm?.category)               base.category               = prefillForm.category;
    if (prefillForm?.summary_of_deficiency)  base.summary_of_deficiency  = prefillForm.summary_of_deficiency;
    return base;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const [form,        setForm]        = useState(initialForm);
  const [creating,    setCreating]    = useState(false);
  const [createError, setCreateError] = useState('');

  // AI Draft
  const [aiStep,         setAiStep]         = useState('form');
  const [analyzerOutput, setAnalyzerOutput] = useState(null);
  const [qaAnswers,      setQaAnswers]      = useState({});
  const [aiDraft,        setAiDraft]        = useState(null);
  const [aiError,        setAiError]        = useState('');

  // Control / test-run pickers
  const [testRunsByControl, setTestRunsByControl] = useState({});
  const [selectedTestRuns,  setSelectedTestRuns]  = useState(() => {
    if (lockedControl && lockedTestRun) {
      return new Set([`${lockedControl.id}|${lockedTestRun.run_number}`]);
    }
    return new Set();
  });
  const [testRunsLoading, setTestRunsLoading] = useState(false);
  const [controlSearch,   setControlSearch]   = useState('');
  const [showControlDrop, setShowControlDrop] = useState(false);
  const controlSearchRef = useRef(null);

  // On mount: if control is locked, auto-fetch its test runs
  useEffect(() => {
    if (lockedControl?.id) fetchTestRunsForControl(lockedControl.id);
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchTestRunsForControl = async (controlId) => {
    if (testRunsByControl[controlId] !== undefined) return;
    if (!controlId || !isControlUuid(controlId)) {
      setTestRunsByControl(prev => ({ ...prev, [controlId]: [] }));
      return;
    }
    setTestRunsLoading(true);
    try {
      // Control-testing runs (test_runs) of the issue's audit year that have results.
      const runs = await listIssueRunOptions(controlId, selectedAuditYearId);
      setTestRunsByControl(prev => ({ ...prev, [controlId]: runs }));
    } catch {
      setTestRunsByControl(prev => ({ ...prev, [controlId]: [] }));
    } finally { setTestRunsLoading(false); }
  };

  const toggleControl = (ctrl) => {
    setForm(f => {
      const already = f.sox_control_ids.includes(ctrl.id);
      if (already) {
        setSelectedTestRuns(prev => {
          const next = new Set(prev);
          (testRunsByControl[ctrl.id] || []).forEach(r => next.delete(`${ctrl.id}|${r.test_run_number}`));
          return next;
        });
        setTestRunsByControl(prev => { const n = { ...prev }; delete n[ctrl.id]; return n; });
        return { ...f, sox_control_ids: f.sox_control_ids.filter(id => id !== ctrl.id) };
      }
      fetchTestRunsForControl(ctrl.id);
      return { ...f, sox_control_ids: [...f.sox_control_ids, ctrl.id] };
    });
    setControlSearch(''); setShowControlDrop(false);
  };

  const toggleTestRun = (controlId, runNumber) => {
    if (lockedTestRun && String(runNumber) === String(lockedTestRun.run_number)) return; // locked
    const key = `${controlId}|${runNumber}`;
    setSelectedTestRuns(prev => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };

  const filteredControls = useMemo(() =>
    controls.filter(c =>
      !form.sox_control_ids.includes(c.id) &&
      (`${c.control_id || ''} ${c.title || ''}`).toLowerCase().includes(controlSearch.toLowerCase())
    ), [controls, form.sox_control_ids, controlSearch]);

  // ── AI Drafter ──────────────────────────────────────────────────────────────

  const runSynthesize = async (analyzerOut, answers) => {
    setAiStep('synthesizing');
    try {
      const body = {
        analyzer_output: analyzerOut,
        clarifying_answers: answers
          ? Object.entries(answers).map(([question_id, answer]) => ({ question_id, answer }))
          : null,
      };
      const res = await axiosInstance.post('/api/issues/ai-draft/synthesize', body);
      const draft = res.data;
      setAiDraft(draft);
      if (draft.suggested_category) {
        setCategories(prev => prev.includes(draft.suggested_category) ? prev : [...prev, draft.suggested_category]);
        try {
          const stored = JSON.parse(localStorage.getItem(CUSTOM_CATEGORIES_KEY) || '[]');
          if (!BASE_CATEGORIES.includes(draft.suggested_category) && !stored.includes(draft.suggested_category)) {
            localStorage.setItem(CUSTOM_CATEGORIES_KEY, JSON.stringify([...stored, draft.suggested_category]));
          }
        } catch {}
      }
      setForm(f => ({
        ...f,
        summary_of_deficiency: draft.summary_of_deficiency    || f.summary_of_deficiency,
        category:              draft.suggested_category        || f.category,
        classification:        draft.suggested_classification  || f.classification,
      }));
      setAiStep('draft');
    } catch (e) {
      setAiError(resolve403ErrorMessage(e, 'AI synthesis failed. You can fill in the form manually.'));
      setAiStep('form');
    }
  };

  const handleAIDraft = async () => {
    const controlIds = lockedControl ? [lockedControl.id] : form.sox_control_ids;
    if (controlIds.length === 0) { setCreateError('Select a control first.'); return; }
    setAiStep('analyzing'); setAiError(''); setCreateError('');
    try {
      const orgId = tenant?.organization_id || localStorage.getItem('organization_id') || '';
      const results = await Promise.all(
        controlIds.map(id => {
          const runs = lockedTestRun
            ? [lockedTestRun.run_number]
            : (testRunsByControl[id] || [])
                .filter(r => selectedTestRuns.has(`${id}|${r.test_run_number}`))
                .map(r => r.test_run_number);
          return axiosInstance.post('/api/issues/ai-draft/analyze', {
            sox_control_id: id,
            org_id: orgId,
            ...(selectedAuditYearId ? { audit_year_id: selectedAuditYearId } : {}),
            ...(runs.length > 0 ? { selected_test_run_numbers: runs } : {}),
          }).then(r => r.data).catch(err => {
            if (err?.response?.status === 403) throw err;
            return null;
          });
        })
      );
      const valid = results.filter(Boolean);
      if (valid.length === 0) throw new Error('All control analyses failed');
      const controlById = Object.fromEntries(controls.map(c => [c.id, c]));
      const merged = {
        ...valid[0],
        test_runs:          valid.flatMap(r => r.test_runs || []),
        exception_patterns: valid.flatMap(r => r.exception_patterns || []),
        questions:          valid.flatMap(r => r.questions || [])
          .filter((q, i, arr) => arr.findIndex(x => x.question_id === q.question_id) === i),
        confidence:         valid.reduce((sum, r) => sum + (r.confidence || 0), 0) / valid.length,
        all_control_ids:    controlIds.map(uid => controlById[uid]?.control_id || (lockedControl?.control_id) || uid),
        all_control_titles: controlIds.map(uid => controlById[uid]?.title || (lockedControl?.title) || uid),
      };
      setAnalyzerOutput(merged);
      if (merged.questions?.length > 0) {
        const initial = {};
        merged.questions.forEach(q => { initial[q.question_id] = ''; });
        setQaAnswers(initial);
        setAiStep('qa');
      } else {
        await runSynthesize(merged, null);
      }
    } catch (e) {
      setAiError(resolve403ErrorMessage(e, 'AI analysis failed. You can fill in the form manually.'));
      setAiStep('form');
    }
  };

  const handleQASubmit = () => runSynthesize(analyzerOutput, qaAnswers);

  // ── Submit ──────────────────────────────────────────────────────────────────

  const handleCreateSubmit = async (e) => {
    e.preventDefault();
    if (!form.title.trim())               { setCreateError('Issue title is required.');            return; }
    if (form.sox_control_ids.length === 0){ setCreateError('Please select at least one control.'); return; }
    if (!form.summary_of_deficiency.trim()){ setCreateError('Summary of deficiency is required.'); return; }
    if (!form.category)                   { setCreateError('Category is required.');               return; }
    if (!form.issue_type)                 { setCreateError('Issue type is required.');             return; }
    setCreating(true); setCreateError('');
    try {
      const body = {
        title:                 form.title.trim(),
        sox_control_ids:       form.sox_control_ids,
        summary_of_deficiency: form.summary_of_deficiency.trim(),
        department:            form.department,
        category:              form.category,
        period:                form.period,
        classification:        form.classification || undefined,
        issue_type:            form.issue_type || 'operating_effectiveness',
        audit_year_id:         selectedAuditYearId || undefined,
      };
      const res = await axiosInstance.post('/api/sox-issues', body);
      const created = res.data;

      // Persist AI draft fields + linked test runs into overview_data
      const overviewData = {};
      if (aiDraft) {
        Object.assign(overviewData, {
          summary_of_deficiency:  aiDraft.summary_of_deficiency,
          root_cause_analysis:    aiDraft.root_cause_analysis,
          condition_text:         aiDraft.condition_text,
          criteria_text:          aiDraft.criteria_text,
          coso_components:        aiDraft.suggested_coso_components,
          fsli:                   aiDraft.suggested_fsli,
          assertions:             aiDraft.suggested_assertions,
          impact_exception_count: aiDraft.impact_exception_count,
          impact_population_size: aiDraft.impact_population_size,
        });
      }
      if (lockedTestRun) {
        overviewData.linked_test_runs = [{
          control_id:       lockedControl?.control_id || '',
          run_number:       lockedTestRun.run_number,
          test_run_id:      lockedTestRun.test_run_id ?? null,
          overall_status:   lockedTestRun.overall_status   ?? null,
          total_exceptions: lockedTestRun.total_exceptions ?? null,
          total_population: lockedTestRun.total_population ?? null,
        }];
      } else if (selectedTestRuns.size > 0) {
        const controlById = Object.fromEntries(controls.map(c => [c.id, c.control_id]));
        overviewData.linked_test_runs = [...selectedTestRuns].map(key => {
          const [uid, runNum] = key.split('|');
          const run = (testRunsByControl[uid] || []).find(r => String(r.test_run_number) === runNum);
          return {
            control_id:       controlById[uid] || uid,
            run_number:       Number(runNum),
            test_run_id:      run?.test_run_id ?? null,
            overall_status:   run?.overall_status   ?? null,
            total_exceptions: run?.total_exceptions ?? null,
            total_population: run?.total_population ?? null,
          };
        });
      }
      if (Object.keys(overviewData).length > 0) {
        await axiosInstance.put(`/api/sox-issues/${created.id}`, { overview_data: overviewData });
        created.overview_data = overviewData;
      }

      onCreated(created);
      if (navigateOnCreate) {
        navigate(TENANT_PATHS.ISSUE_DETAIL.replace(':id', created.id), { state: { issue: created } });
      }
    } catch (e) {
      setCreateError(resolve403ErrorMessage(e, e.response?.data?.error || 'Failed to create issue.'));
    } finally { setCreating(false); }
  };

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 z-[70] overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-4 py-6">
        <div className="fixed inset-0 bg-gray-900 bg-opacity-50" onClick={onClose} />
        <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-2xl mx-auto">

          {/* Header */}
          <div className="flex items-start justify-between px-6 pt-6 pb-4 border-b border-gray-100">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {lockedControl ? 'Log Issue from Test Run' : 'Create New Issue'}
              </h3>
              <p className="text-sm text-gray-500 mt-0.5">
                {aiStep === 'qa'          ? 'Answer a few questions so the AI can draft the finding'
                : aiStep === 'draft'      ? 'AI draft ready — review and edit before creating'
                : lockedControl          ? 'Review and submit — fields are pre-filled from the test run'
                :                          'Log a deficiency'}
              </p>
            </div>
            <button onClick={onClose} className="p-1.5 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Loading steps */}
          {(aiStep === 'analyzing' || aiStep === 'synthesizing') && (
            <div className="flex flex-col items-center justify-center py-20 gap-4">
              <Loader2 className="h-8 w-8 text-blue-500 animate-spin" />
              <p className="text-sm font-medium text-gray-700">
                {aiStep === 'analyzing' ? 'Analyzing test results…' : 'Drafting deficiency finding…'}
              </p>
              <p className="text-xs text-gray-400">This may take up to 30 seconds</p>
            </div>
          )}

          {/* Q&A step */}
          {aiStep === 'qa' && analyzerOutput && (
            <div className="px-6 py-5 space-y-4">
              {analyzerOutput.suggested_condition && (
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <p className="text-xs font-semibold text-blue-700 mb-1">AI Observed</p>
                  <p className="text-sm text-blue-800">{analyzerOutput.suggested_condition}</p>
                </div>
              )}
              {analyzerOutput.questions.map(q => (
                <div key={q.question_id}>
                  <label className="block text-sm font-medium text-gray-700 mb-1">{q.question}</label>
                  <p className="text-xs text-gray-400 mb-2">{q.reason}</p>
                  {q.input_type === 'yes_no' ? (
                    <div className="flex gap-2">
                      {['Yes', 'No'].map(opt => (
                        <button key={opt} type="button"
                          onClick={() => setQaAnswers(a => ({ ...a, [q.question_id]: opt }))}
                          className={`px-4 py-2 text-sm rounded-lg border-2 font-medium transition-all ${qaAnswers[q.question_id] === opt ? 'border-blue-500 bg-blue-50 text-blue-700' : 'border-gray-200 text-gray-600 hover:border-gray-300'}`}
                        >{opt}</button>
                      ))}
                    </div>
                  ) : q.input_type === 'select' && q.options ? (
                    <div className="relative">
                      <select value={qaAnswers[q.question_id] || ''}
                        onChange={e => setQaAnswers(a => ({ ...a, [q.question_id]: e.target.value }))}
                        className="w-full appearance-none px-3 py-2 pr-8 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white">
                        <option value="">Select…</option>
                        {q.options.map(o => <option key={o} value={o}>{o}</option>)}
                      </select>
                      <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    </div>
                  ) : (
                    <textarea rows={3} value={qaAnswers[q.question_id] || ''}
                      onChange={e => setQaAnswers(a => ({ ...a, [q.question_id]: e.target.value }))}
                      placeholder="Your answer…"
                      className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none" />
                  )}
                </div>
              ))}
              {aiError && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{aiError}</p>}
              <div className="flex justify-end gap-3 pt-1">
                <button type="button" onClick={() => setAiStep('form')}
                  className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50">Back</button>
                <button type="button" onClick={handleQASubmit}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">
                  <Zap className="h-4 w-4" />Continue
                </button>
              </div>
            </div>
          )}

          {/* Main form */}
          {(aiStep === 'form' || aiStep === 'draft') && (
            <form onSubmit={handleCreateSubmit}>
              <div className="px-6 py-5 space-y-4">

                {/* AI Draft banner */}
                {aiStep === 'draft' && aiDraft && (
                  <div className="flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-xl p-3">
                    <Zap className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-xs font-semibold text-blue-700">AI Draft Applied</p>
                      <p className="text-xs text-blue-600 mt-0.5">Confidence: {Math.round((aiDraft.confidence || 0) * 100)}%</p>
                      {aiDraft.suggested_classification && (
                        <p className="text-xs text-blue-600 mt-0.5">
                          Suggested classification: <span className="font-semibold">{SEVERITY_CONFIG[aiDraft.suggested_classification]?.label || aiDraft.suggested_classification}</span>
                        </p>
                      )}
                      {aiDraft.reasoning && <p className="text-xs text-blue-500 mt-1 italic">{aiDraft.reasoning}</p>}
                    </div>
                    <button type="button" onClick={() => { setAiStep('form'); setAiDraft(null); }}
                      className="text-blue-400 hover:text-blue-600 shrink-0"><X className="h-4 w-4" /></button>
                  </div>
                )}

                {/* Issue title */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue Title <span className="text-red-500">*</span></label>
                  <input type="text" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))}
                    placeholder="e.g. Payroll Approval Control Deficiency Q2 2026"
                    className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500" />
                </div>

                {/* Control — locked pill or free search */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Control <span className="text-red-500">*</span></label>
                  {lockedControl ? (
                    <div className="flex items-center gap-2 px-3 py-2 bg-gray-50 border border-gray-200 rounded-lg">
                      <Lock className="h-3.5 w-3.5 text-gray-400 shrink-0" />
                      <span className="text-sm font-medium text-gray-700">
                        {lockedControl.control_id}{lockedControl.title ? ` — ${lockedControl.title}` : ''}
                      </span>
                    </div>
                  ) : (
                    <>
                      {form.sox_control_ids.length > 0 && (
                        <div className="flex flex-wrap gap-1.5 mb-2">
                          {form.sox_control_ids.map(id => {
                            const ctrl = controls.find(c => c.id === id);
                            if (!ctrl) return null;
                            return (
                              <span key={id} className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium bg-blue-50 text-blue-700 border border-blue-200 rounded-full">
                                {ctrl.control_id || ctrl.title}
                                <button type="button" onClick={() => toggleControl(ctrl)} className="hover:text-blue-900"><X className="h-3 w-3" /></button>
                              </span>
                            );
                          })}
                        </div>
                      )}
                      <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                        <input ref={controlSearchRef} type="text" value={controlSearch}
                          onChange={e => { setControlSearch(e.target.value); setShowControlDrop(true); }}
                          onFocus={() => setShowControlDrop(true)}
                          onBlur={() => setTimeout(() => setShowControlDrop(false), 150)}
                          placeholder="Search and add controls…"
                          className="w-full pl-9 pr-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white" />
                        {showControlDrop && filteredControls.length > 0 && (
                          <div className="absolute z-10 w-full mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-48 overflow-y-auto">
                            {filteredControls.slice(0, 20).map(ctrl => (
                              <button key={ctrl.id} type="button" onMouseDown={() => toggleControl(ctrl)}
                                className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 hover:text-blue-700">
                                <span className="font-medium">{ctrl.control_id}</span>
                                {ctrl.title ? ` — ${ctrl.title}` : ''}
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                    </>
                  )}
                </div>

                {/* Test Runs */}
                {form.sox_control_ids.length > 0 && (
                  <div>
                    <div className="flex items-center gap-2 mb-2">
                      <label className="text-sm font-medium text-gray-700">Test Runs</label>
                      {testRunsLoading && <Loader2 className="h-3.5 w-3.5 text-gray-400 animate-spin" />}
                    </div>
                    {form.sox_control_ids.map(id => {
                      const ctrl = lockedControl?.id === id ? lockedControl : controls.find(c => c.id === id);
                      const runs = testRunsByControl[id];
                      if (!runs) return (
                        <div key={id} className="text-xs text-gray-400 px-1 mb-2">
                          {ctrl?.control_id || id}: loading…
                        </div>
                      );
                      if (runs.length === 0 && !lockedTestRun) return (
                        <div key={id} className="text-xs text-gray-400 px-1 mb-2">
                          {ctrl?.control_id || id}: no test runs
                        </div>
                      );
                      const displayRuns = lockedTestRun
                        ? [{ test_run_number: lockedTestRun.run_number, overall_status: lockedTestRun.overall_status, total_exceptions: lockedTestRun.total_exceptions }]
                        : runs;
                      return (
                        <div key={id} className="mb-3">
                          {!lockedControl && (
                            <p className="text-xs font-semibold text-gray-600 mb-1.5">
                              {ctrl?.control_id || id}{ctrl?.title ? ` — ${ctrl.title}` : ''}
                            </p>
                          )}
                          <div className="space-y-1.5">
                            {displayRuns.map(run => {
                              const key = `${id}|${run.test_run_number}`;
                              const checked = selectedTestRuns.has(key);
                              const isLocked = lockedTestRun && String(run.test_run_number) === String(lockedTestRun.run_number);
                              const badge = RUN_STATUS_BADGE[(run.overall_status || '').toLowerCase()] || {};
                              return (
                                <button key={key} type="button"
                                  onClick={() => toggleTestRun(id, run.test_run_number)}
                                  className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg border text-left transition-colors ${
                                    checked ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white hover:border-gray-300'
                                  } ${isLocked ? 'cursor-default' : ''}`}>
                                  <div className={`w-4 h-4 rounded border-2 flex-shrink-0 flex items-center justify-center ${checked ? 'bg-blue-600 border-blue-600' : 'border-gray-300'}`}>
                                    {checked && <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 10"><path d="M1.5 5L4 7.5L8.5 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>}
                                  </div>
                                  <span className="text-xs font-medium text-gray-700">Run #{run.test_run_number}</span>
                                  {run.test_run_name && <span className="text-xs text-gray-500 truncate">{run.test_run_name}</span>}
                                  {isLocked && <Lock className="h-3 w-3 text-gray-400" />}
                                  {badge.label && <span className={`text-xs font-medium px-1.5 py-0.5 rounded-full ${badge.cls}`}>{badge.label}</span>}
                                  {run.total_exceptions != null && <span className="text-xs text-gray-500 ml-auto">{run.total_exceptions} exceptions</span>}
                                </button>
                              );
                            })}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Summary of Deficiency */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Summary of Deficiency <span className="text-red-500">*</span></label>
                  <textarea rows={4} value={form.summary_of_deficiency}
                    onChange={e => setForm(f => ({ ...f, summary_of_deficiency: e.target.value }))}
                    placeholder="Describe the deficiency…"
                    className={`w-full px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 resize-none ${aiStep === 'draft' ? 'border-blue-300 bg-blue-50/30' : 'border-gray-300'}`} />
                  {aiStep === 'form' && (
                    <button type="button" onClick={handleAIDraft}
                      disabled={form.sox_control_ids.length === 0}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-blue-700 bg-blue-50 border border-blue-200 rounded-lg hover:bg-blue-100 disabled:opacity-40 disabled:cursor-not-allowed transition-colors">
                      <Zap className="h-3.5 w-3.5" />AI Draft from Test Results
                    </button>
                  )}
                  {aiStep === 'draft' && (
                    <button type="button" onClick={handleAIDraft}
                      className="mt-2 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-gray-100 border border-gray-200 rounded-lg hover:bg-gray-200 transition-colors">
                      <RefreshCw className="h-3.5 w-3.5" />Re-draft
                    </button>
                  )}
                </div>

                {/* Period */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Period</label>
                  <div className="relative">
                    <select value={form.period} onChange={e => setForm(f => ({ ...f, period: e.target.value }))}
                      className="w-full appearance-none px-3 py-2 pr-7 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700">
                      <option value="">Select period…</option>
                      {PERIOD_OPTIONS.map(p => <option key={p} value={p}>{p}</option>)}
                    </select>
                    <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                  </div>
                </div>

                {/* Issue Type */}
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Issue Type <span className="text-red-500">*</span></label>
                  <div className="flex gap-2">
                    {['operating_effectiveness', 'design'].map(t => (
                      <button key={t} type="button"
                        onClick={() => setForm(f => ({ ...f, issue_type: t }))}
                        className={`flex-1 py-2 text-sm font-medium rounded-lg border transition-colors ${form.issue_type === t ? (t === 'design' ? 'bg-purple-600 border-purple-600 text-white' : 'bg-blue-600 border-blue-600 text-white') : 'bg-white border-gray-300 text-gray-700 hover:border-gray-400'}`}>
                        {t === 'operating_effectiveness' ? 'Operating Effectiveness' : 'Design Deficiency'}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Department / Category */}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Department</label>
                    <div className="relative">
                      <select value={form.department} onChange={e => setForm(f => ({ ...f, department: e.target.value }))}
                        className="w-full appearance-none px-3 py-2 pr-7 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700">
                        <option value="">Select…</option>
                        {DEPARTMENTS.map(d => <option key={d} value={d}>{d}</option>)}
                      </select>
                      <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Category <span className="text-red-500">*</span></label>
                    {showAddCategory ? (
                      <div className="flex gap-1.5">
                        <input type="text" value={newCategoryInput}
                          onChange={e => setNewCategoryInput(e.target.value)}
                          placeholder="New category…" autoFocus
                          className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500" />
                        <button type="button" onClick={() => {
                          const v = newCategoryInput.trim();
                          if (v && !categories.includes(v)) {
                            const next = [...categories, v];
                            setCategories(next);
                            setForm(f => ({ ...f, category: v }));
                            try {
                              const stored = JSON.parse(localStorage.getItem(CUSTOM_CATEGORIES_KEY) || '[]');
                              if (!stored.includes(v)) localStorage.setItem(CUSTOM_CATEGORIES_KEY, JSON.stringify([...stored, v]));
                            } catch {}
                          } else if (v) {
                            setForm(f => ({ ...f, category: v }));
                          }
                          setNewCategoryInput(''); setShowAddCategory(false);
                        }} className="px-3 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700">Add</button>
                        <button type="button" onClick={() => { setNewCategoryInput(''); setShowAddCategory(false); }}
                          className="px-2 py-2 text-gray-400 hover:text-gray-600"><X className="h-4 w-4" /></button>
                      </div>
                    ) : (
                      <div className="flex gap-1.5">
                        <div className="relative flex-1">
                          <select value={form.category} onChange={e => setForm(f => ({ ...f, category: e.target.value }))}
                            className={`w-full appearance-none px-3 py-2 pr-7 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 bg-white text-gray-700 ${aiStep === 'draft' && form.category ? 'border-blue-300' : 'border-gray-300'}`}>
                            <option value="">Select…</option>
                            {categories.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                          <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400 pointer-events-none" />
                        </div>
                        <button type="button" onClick={() => setShowAddCategory(true)}
                          title="Add new category"
                          className="px-2 py-2 text-gray-400 hover:text-blue-600 border border-gray-200 rounded-lg hover:border-blue-300 transition-colors">
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>
                    )}
                  </div>
                </div>

                {aiError     && <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">{aiError}</p>}
                {createError && <p className="text-sm text-red-600 bg-red-50 rounded-lg px-3 py-2">{createError}</p>}
              </div>

              <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-gray-100">
                <button type="button" onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">Cancel</button>
                <button type="submit" disabled={creating}
                  className="flex items-center gap-2 px-5 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50">
                  {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  {creating ? 'Creating…' : 'Create Issue'}
                </button>
              </div>
            </form>
          )}

        </div>
      </div>
    </div>
  );
}
